import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { PaymentMode, UserPaymentModeService } from './user/user-payment-mode.service';
import { BannerType } from '../../../environments/environment';
import { LocalstorageService } from './localstorage.service';
import { UserBonusesService } from './user/user-bonuses.service';
import { BannersService } from './banners.service';
import { UserGroupsService } from './user/user-groups.service';
import { HtmlParserService } from './html-parser.service';
import { UserService } from './user/user.service';
import { isArray } from '../helpers/utils';
import { StaticContentService } from './static-content.service';
import { DecimalPipe } from '@angular/common';
import { CommonDataService, CurrencyList$ } from './common-data.service';
import { TranslationService } from './translation/translation.service';
import { UserLoyaltyService } from './user/user-loyalty.service';
import { JackpotService } from '../../page/jackpot/jackpot.service';
import { StaticPageService } from '../../page/static-page/static-page.service';
import { isMobile } from '../signals/device';

@Injectable({
  providedIn: 'root',
})
export class ActiveBannerService {
  paymentMode = inject(UserPaymentModeService);
  bonuses = inject(UserBonusesService);
  private _storage = inject(LocalstorageService);
  private _banners = inject(BannersService);
  private _groups = inject(UserGroupsService);
  private _html = inject(HtmlParserService);
  private _user = inject(UserService);
  private _static = inject(StaticContentService);
  private _number = inject(DecimalPipe);
  private _common = inject(CommonDataService);
  private _translate = inject(TranslationService);
  private _loyalty = inject(UserLoyaltyService);
  private _jackpot = inject(JackpotService);
  private _page = inject(StaticPageService);


  public storeBannersToStorage() {
    if (this._groups.isAllWelcomeUsed()) {
      this._storage.clearItem('banners');
    }
    of(true).pipe(
      filter(() => !this._groups.isAllWelcomeUsed()),
      switchMap(() => this.getBannersWithBonuses$()),
      map(data => data.filter(item => (item?.alias !== 'header' && item?.alias !== 'header-first-dep'))),
      tap(filteredData => this._storage.set('banners', JSON.stringify(filteredData))),
    ).subscribe();
  }

  public getStaticBanners$() {
    return this.paymentMode.currentMode$.pipe(
      switchMap((mode: string) => this._banners.list({
        type: mode === PaymentMode.FIAT ? BannerType.TOP_SLIDER_FIAT : BannerType.TOP_SLIDER,
      })),
      filter(list => !!list),
      map(data => {
        if (this._groups.isAllWelcomeUsed()) {
          return data.filter(item => (item?.alias !== 'header' && item?.alias !== 'header-first-dep'));
        } else {
          return data;
        }
      }),
      map(list => {
        if (isArray(list)) {
          return this._filterSlideList(list);
        } else {
          return list;
        }
      }),
    ) as ReplaySubject<any>;
  }

  public getBannersWithBonuses$() {
    return this.getStaticBanners$().pipe(
      switchMap(bannerList => combineLatest([
        of(bannerList),
        this.bonuses.welcomeBonuses$,
      ])),
      switchMap(slideList => combineLatest([
        of(slideList[0]),
        of(slideList[1]),
      ])),
      map(([banners, welcomeBonuses]) => {
        const activeWelcomeBonuses = welcomeBonuses.find(bonus => bonus.active);

        if (activeWelcomeBonuses) {
          this._html.replaceLineBreaks(activeWelcomeBonuses);
        }

        switch (true) {
          case this.bonuses.isAllBonusUsed:
            return [
              ...banners,
            ];
          default:
            return [
              activeWelcomeBonuses,
              ...banners,
            ];
        }

      }),
    ) as ReplaySubject<any>;
  }

  public clearBannersItem() {
    this._storage.clearItem('banners');
  }

  /**
   * Filter slide list by params from CMS
   *
   * @param list
   * @private
   */
  private _filterSlideList(list: Array<any>): Array<any> {
    return list.filter(slide => {
      if (this._user.auth && slide?.SignedInOnlyButton) {
        return true;
      }

      if (!this._user.auth && slide?.SignedInOnlyButton) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get welcome bonus slider for five slider
   * @private
   */
  public getWelcomeBonusSlider(): Observable<any> {
    return this._page.item({ slug: this.paymentMode.isModeCrypto ? 'bonuses' : 'fiat-bonuses' }).pipe(
      filter(data => !!data),
      map(data => ({
        Title: this._translate.translate('t.banner-welcome-title'),
        SubTitle: data?.[0]?.TitleStarter,
        Image: `/assets/img/bonuses/${this.paymentMode.isModeCrypto ? 'crypto' : 'fiat'}/banner-desktop.png`,
        ImageMob: `/assets/img/bonuses/${this.paymentMode.isModeCrypto ? 'crypto' : 'fiat'}/banner-mobile.png`,
        order: 1,
      })),
    );
  }

  /**
   * Get promo slider for five slider
   * @private
   */
  public getPromoSlider() {
    return isMobile() ? this._banners.list({
      type: BannerType.TOP_FEATURE,
    }).pipe(
      filter(list => !!list),
      map(list => list?.find(f => f.Url?.includes('promo'))),
      map(slider => slider ? ({ ...slider, ImageMob: slider?.Image, order: 2 }) : slider),
      first(),
    ) : of(null);
  }

  /**
   * Get tournament prize slider for five slider
   * @private
   */
  public getTournamentsPrizeSlider() {
    return this._static.item({ slug: `total-pool` }).pipe(
      filter(data => !!data),
      map((data) => data.map(item => {
        return {
          ...item,
          Title: this._translate.translate('t.banner-tournament-title'),
          SubTitle: `${item?.Currency} ${this._number.transform(item?.PrizePool, this._common.isCryptoCurrency(item?.Currency) ? '0.8' : '0.2')}`,
          SecondDescription: `+ ${+item.FreeSpins} ${this._translate.translate('t.free-spins')}`,
          order: 3,
        };
      })[0]),
    );
  }

  /**
   * Get loyalty prize slider for five slider
   * @private
   */
  public getLoyaltyPrizeSlider() {
    return this._loyalty.getLoyaltyPage$().pipe(
      filter(data => !!data),
      map(item => ({
        ...item,
        Title: this._translate.translate('t.banner-loyalty-title'),
        SubTitle: item?.Prize,
        order: 4,
      })),
    );
  }

  /**
   * Get jackpot prize slider for five slider
   * @private
   */
  public getJackpotPrizeSlider() {
    return combineLatest([
      this._jackpot.getGamesTotalJackpots(),
      this._jackpot.cmsJackpot$,
      CurrencyList$,
    ]).pipe(
      map(([gamesTotal, cmsJackpot, currencyList = []]) => {
        const localeCurrency = currencyList?.map(c => ({ ...c, symbol: c?.symbol === 'A$' ? 'AU$' : c?.symbol }))
          .reverse().find(c => cmsJackpot?.Levels?.some(l => l?.min.includes(c?.symbol) || l?.min.includes(c?.code)))?.code || 'EUR';
        const gamesTotalUnits = this._common.subunitsToUnits(gamesTotal?.[localeCurrency], localeCurrency);

        return {
          Title: this._translate.translate('t.banner-jackpot-title'),
          SubTitle: `${localeCurrency} ${this._number.transform(gamesTotalUnits, this._common.isCryptoCurrency(localeCurrency) ? '0.8' : '0.2')}`,
          Image: cmsJackpot?.TopBgImage,
          ImageMob: cmsJackpot?.TopBgImageMob,
          order: 5,
        };
      }),
    );
  }
}
